import React from 'react'
import { buildLayoutConfig, COMMON_LAYOUT_EDITABLE_FIELDS, IntroStatsPanel, LayoutSections } from './layoutShared'

const LEAD_ANCHOR_ID = 'sgez-category-landing-lead-anchor'

const DEFAULT_CONFIG = {
  heroBackgroundColor: '#f8fafc',
  heroTextColor: '#0f172a',
  heroSubtitleColor: '#475569',
  formBorderColor: '#3b82f6',
  formBorderWidth: 3,
  heroScrollCtaLabel: '',
}

function GeneralStyleLayout({
  mode = 'public',
  heroTitle,
  heroSubtitle,
  heroImageFileId,
  sections = [],
  formEnabled = true,
  introStats = [],
  customConfig = {},
  resolveAssetUrl,
  formCard = null,
  recommendedProductsCard = null,
  previewBanner = null,
  surveyModal = null,
  surveyDoneModal = null,
  formTitle = '',
}) {
  const config = buildLayoutConfig('default', customConfig, DEFAULT_CONFIG)
  const isLive = mode === 'live'

  const scrollToLeadForm = () => {
    const el = typeof document !== 'undefined' ? document.getElementById(LEAD_ANCHOR_ID) : null
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    const schedule = typeof window !== 'undefined' ? window.setTimeout : setTimeout
    schedule(() => {
      const root = typeof document !== 'undefined' ? document.getElementById(LEAD_ANCHOR_ID) : null
      if (!root) return
      const focusable = root.querySelector(
        'input[type="text"], input[type="tel"], input[type="email"], input:not([type]), textarea',
      )
      if (focusable && typeof focusable.focus === 'function') focusable.focus()
    }, 450)
  }

  const heroCtaLabel = (config.heroScrollCtaLabel && String(config.heroScrollCtaLabel).trim())
    || (formTitle && String(formTitle).trim())
    || '바로 신청하기'

  return (
    <div className="min-h-full bg-white text-slate-900 relative">
      {previewBanner}
      <div className="px-4 py-12 md:py-16 lg:py-20" style={{ backgroundColor: config.heroBackgroundColor }}>
        <div
          className={`mx-auto text-center ${isLive ? 'max-w-full' : 'w-full max-w-7xl 2xl:max-w-[90rem]'}`}
        >
          {heroTitle && (
            <h1
              className="whitespace-pre-line text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight leading-tight break-keep text-balance"
              style={{ color: config.heroTextColor }}
            >
              {heroTitle}
            </h1>
          )}
          {heroSubtitle && <p className="mt-5 text-xl md:text-2xl leading-8 whitespace-pre-line" style={{ color: config.heroSubtitleColor }}>{heroSubtitle}</p>}
          {(heroImageFileId || formEnabled) && (
            <div className={`mx-auto w-full max-w-full ${heroTitle || heroSubtitle ? 'mt-6 md:mt-8' : ''}`}>
              {heroImageFileId && (
                <div className="overflow-hidden rounded-2xl bg-slate-200/40">
                  <img
                    src={resolveAssetUrl(heroImageFileId)}
                    alt=""
                    className="block max-h-[min(38vh,420px)] w-full object-cover object-center sm:max-h-[min(42vh,460px)] lg:max-h-[min(46vh,520px)]"
                  />
                </div>
              )}
              {formEnabled && (
                <button
                  type="button"
                  className={`flex min-h-[48px] w-full items-center justify-center rounded-2xl px-6 py-3.5 text-base font-semibold text-white shadow-md transition hover:opacity-90 active:opacity-95 ${heroImageFileId ? 'mt-8' : ''}`}
                  style={{ backgroundColor: config.formBorderColor }}
                  onClick={scrollToLeadForm}
                >
                  {heroCtaLabel}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <div className={`px-4 py-10 md:py-12 ${isLive ? '' : ''}`}>
        <div className={`mx-auto ${isLive ? 'max-w-full space-y-4' : 'max-w-6xl flex flex-col lg:flex-row gap-8'}`}>
          <div className="flex-1">
            <div className={`${isLive ? 'space-y-4' : 'space-y-6'}`}>
              <LayoutSections sections={sections} resolveAssetUrl={resolveAssetUrl} variant={isLive ? 'compact' : 'default'} />
              <IntroStatsPanel introStats={introStats} />
            </div>
          </div>

          {(formEnabled || recommendedProductsCard) && (
            <div className={isLive ? 'space-y-3' : 'lg:w-[360px] lg:flex-shrink-0'}>
              <div
                id={LEAD_ANCHOR_ID}
                tabIndex={-1}
                className={`scroll-mt-4 outline-none ${isLive ? 'space-y-3' : 'lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto space-y-3'}`}
              >
                {formEnabled && formCard}
                {recommendedProductsCard}
              </div>
            </div>
          )}
        </div>
      </div>

      {surveyModal}
      {surveyDoneModal}
    </div>
  )
}

export const generalStyleLayout = {
  key: 'default',
  label: '기본 템플릿 1 - 일반형',
  description: '상단 소개와 본문, 상담 영역이 균형 있게 배치된 기본 레이아웃입니다.',
  editableConfigFields: [
    ...COMMON_LAYOUT_EDITABLE_FIELDS,
    { key: 'heroScrollCtaLabel', label: '히어로 하단 CTA 버튼 문구', placeholder: '숨은보험금 찾아보기' },
  ],
  normalizeCustomConfig: (customConfig) => buildLayoutConfig('default', customConfig, DEFAULT_CONFIG),
  Component: GeneralStyleLayout,
}
